import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { FailureChain } from '~/features/workflow-inspection/components/failure-chain';
import { OpenInTemporalButton } from '~/features/workflow-inspection/components/open-in-temporal-button';
import { SensitiveRevealToggle } from '~/features/workflow-inspection/components/sensitive-reveal-toggle';
import { useSensitiveMask } from '~/features/workflow-inspection/hooks/use-sensitive-mask';
import { cn } from '~/utils/cn';
import { formatDateTime, formatDurationMs } from '~/utils/date';

import { isPhasePulsing, phaseTone } from '../../constants/status-tones';
import { useDeliveryRunDetail } from '../../hooks/use-delivery-run-detail';
import { useDeliveryRunStream } from '../../hooks/use-delivery-run-stream';
import type { DeliveryRunDetail, DeliveryRunStage } from '../../types/delivery-runs.types';
import { humanizeEnum } from '../../utils/format-enum';
import { DeliveryStatusChip } from '../chips/delivery-status-chip';
import { LiveIndicator } from '../chips/live-indicator';
import { DeliveryControlRail } from '../delivery-control-rail';
import { RunErrorSummary } from '../run-error-summary';

import { DeliveryRunWorkflowPanel } from './delivery-run-workflow-panel';

const STAGE_TONE: Record<string, string> = {
  COMPLETED: 'bg-success/10 text-success border-success/30',
  RUNNING: 'bg-primary/10 text-primary border-primary/30',
  FAILED: 'bg-destructive/10 text-destructive border-destructive/30',
  SKIPPED: 'bg-muted text-muted-foreground',
  PENDING: 'bg-background text-muted-foreground',
};

interface Props {
  runId: string;
  /** Follow a run created by Retry failed / Retry delivery (the host decides how). */
  onRunChange: (runId: string) => void;
  /** Catalog batch the run delivers, when the host knows it — Retry delivery opens its wizard. */
  batchId?: string | null | undefined;
  /** `data-testid` root; header, facts, stages and retry button derive from it. */
  testId?: string;
}

/**
 * Delivery-run view of the Temporal inspector, reusable by the delivery wizard
 * and the deliveries console. Header from the permanent run record (§2.1, kept
 * live by the run stream): phase, ids, timing, actor, progress, saved stage
 * strip, the stored failure chain and the run controls; then the generic
 * `WorkflowInspector` for the run's own workflow, with the retry chain in its
 * Relationships tab. Everything rendered is masked unless the session
 * "Reveal sensitive data" switch (inline with the run controls) is on; controls get
 * the raw run (they need the real connection id).
 */
export function DeliveryRunInspector({
  runId,
  onRunChange,
  batchId,
  testId = 'delivery-run-inspector',
}: Props) {
  const { t } = useTranslation('deliveries');
  const detail = useDeliveryRunDetail(runId);
  const raw = detail.data;
  const streamStatus = useDeliveryRunStream(runId, raw !== undefined && !raw.isTerminal);
  const { mask } = useSensitiveMask([runId, raw?.workflowId]);

  if (detail.isLoading) {
    return (
      <div data-testid={`${testId}-loading`} className="space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (!raw) {
    return (
      <div
        data-testid={`${testId}-error`}
        className="flex flex-wrap items-center gap-3 rounded-lg border p-4 text-sm"
      >
        <span className="text-foreground">{t('inspector.loadError')}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="cursor-pointer"
          isLoading={detail.isFetching}
          data-testid={`${testId}-retry-button`}
          onClick={() => void detail.refetch()}
        >
          {t('inspector.retry')}
        </Button>
      </div>
    );
  }

  const run = mask(raw);
  const statusLabel = t(`phase.${run.phase}`, { defaultValue: humanizeEnum(run.phase) });

  return (
    <div data-testid={testId} className="space-y-6">
      <RunHeader
        run={run}
        statusLabel={statusLabel}
        streamStatus={streamStatus}
        testId={`${testId}-header`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <OpenInTemporalButton workflowId={raw.workflowId} />
          <SensitiveRevealToggle />
          <DeliveryControlRail run={raw} onRunCreated={onRunChange} batchId={batchId} />
        </div>
      </RunHeader>

      {run.failure ? (
        <FailureChain failure={run.failure} />
      ) : run.errorCode ? (
        <RunErrorSummary code={run.errorCode} message={run.errorMessage} />
      ) : null}

      <DeliveryRunWorkflowPanel
        run={raw}
        onSelectRun={onRunChange}
        showRevealToggle={false}
        testId={`${testId}-workflow-panel`}
      />
    </div>
  );
}

/** Run identity, timing, actor, progress and stage strip, with controls on the right. */
function RunHeader({
  run,
  statusLabel,
  streamStatus,
  children,
  testId,
}: {
  run: DeliveryRunDetail;
  statusLabel: string;
  streamStatus: Parameters<typeof LiveIndicator>[0]['status'];
  children: ReactNode;
  testId: string;
}) {
  const { t } = useTranslation('deliveries');
  const facts: { key: string; label: string; value: string | null }[] = [
    { key: 'connection', label: t('inspector.connection'), value: run.connection ?? run.platform },
    {
      key: 'started',
      label: t('inspector.started'),
      value: run.startedAt ? formatDateTime(run.startedAt) : null,
    },
    {
      key: 'completed',
      label: t('inspector.completed'),
      value: run.completedAt ? formatDateTime(run.completedAt) : null,
    },
    {
      key: 'duration',
      label: t('inspector.duration'),
      value: run.durationSeconds !== null ? formatDurationMs(run.durationSeconds * 1000) : null,
    },
    {
      key: 'triggered-by',
      label: t('inspector.triggeredBy'),
      value: run.triggeredBy?.displayName ?? null,
    },
    {
      key: 'current-stage',
      label: t('inspector.currentStage'),
      value: run.isTerminal ? null : (run.currentStage?.stageLabel ?? null),
    },
  ];
  const stages = run.stages ?? [];

  return (
    <section data-testid={testId} className="bg-background space-y-4 rounded-xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <DeliveryStatusChip
              tone={phaseTone(run.phase)}
              label={statusLabel}
              pulse={isPhasePulsing(run.phase)}
              testId={`${testId}-status-chip`}
            />
            <LiveIndicator status={streamStatus} />
            {!run.isTerminal && (
              <span
                data-testid={`${testId}-progress`}
                className="text-muted-foreground text-xs tabular-nums"
              >
                {t('inspector.progress', { pct: Math.round(run.percentComplete) })}
              </span>
            )}
          </div>
          <IdLine
            label={t('inspector.runId')}
            value={run.id}
            copyLabel={t('inspector.copyRunId')}
            testId={`${testId}-run-id`}
          />
          {run.workflowId && (
            <IdLine
              label={t('inspector.workflowId')}
              value={run.workflowId}
              copyLabel={t('inspector.copyWorkflowId')}
              testId={`${testId}-workflow-id`}
            />
          )}
        </div>
        {children}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3 xl:grid-cols-6">
        {facts
          .filter((f) => f.value)
          .map((f) => (
            <div key={f.key} data-testid={`${testId}-fact-${f.key}`} className="min-w-0">
              <dt className="text-muted-foreground text-xs">{f.label}</dt>
              <dd
                data-testid={`${testId}-fact-${f.key}-value`}
                className="text-foreground font-medium break-words"
              >
                {f.value}
              </dd>
            </div>
          ))}
      </dl>

      <p data-testid={`${testId}-totals`} className="text-muted-foreground text-xs tabular-nums">
        {t('inspector.totals', {
          delivered: run.totals.delivered,
          failed: run.totals.failed,
          total: run.totals.total,
        })}
      </p>

      {stages.length > 0 && <StageStrip stages={stages} testId={`${testId}-stages`} />}
    </section>
  );
}

function IdLine({
  label,
  value,
  copyLabel,
  testId,
}: {
  label: string;
  value: string;
  copyLabel: string;
  testId: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1 text-xs">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span data-testid={testId} className="text-foreground font-mono break-all">
        {value}
      </span>
      <CopyIconButton text={value} label={copyLabel} />
    </div>
  );
}

/** The workflow's saved stage timeline as chips (present once the run ends). */
function StageStrip({ stages, testId }: { stages: DeliveryRunStage[]; testId: string }) {
  const { t } = useTranslation('deliveries');
  return (
    <div data-testid={testId} className="space-y-1.5">
      <p className="text-muted-foreground text-xs">{t('inspector.stages')}</p>
      <ol className="flex flex-wrap gap-1.5">
        {stages.map((s) => (
          <li
            key={s.name}
            data-testid={`${testId}-stage-${s.name}`}
            title={t(`inspector.stageStatus.${s.status}`, { defaultValue: humanizeEnum(s.status) })}
            className={cn(
              'rounded-md border px-2 py-0.5 text-xs font-medium',
              STAGE_TONE[s.status] ?? STAGE_TONE.PENDING,
            )}
          >
            {s.label}
          </li>
        ))}
      </ol>
    </div>
  );
}
